import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { useSelector } from 'react-redux';
import { db } from '@/shared/lib';
import { Store } from '@/redux';
import { LoggerService } from '@/shared/logger';

/**
 * /chat_threads is a dedicated thread-summary collection keyed by the
 * *sorted* pair of participant UIDs. One doc per conversation, written by
 * both sides on every chat message. The DM sidebar lists threads by
 * querying this collection ordered by `lastMessageAt` desc — which
 * means a long burst of messages in one thread no longer pushes the
 * other threads out of the picture (the bug the per-message
 * group-by approach had).
 *
 * Schema:
 *   chat_threads / {sortedAUID_sortedBUID}
 *     {
 *       participants:    [uidA, uidB]   // sorted ascending
 *       lastMessage:     string         (truncated to 200 chars to keep the doc small)
 *       lastSenderID:    string
 *       lastMessageAt:   Timestamp      (serverTimestamp on every write)
 *       lastIsRead:      'true' | 'false'
 *       startedAt:       Timestamp      (set on first create)
 *     }
 */
export const CHAT_THREADS_COLLECTION = 'chat_threads';

export const chatThreadDocID = (uidA: string, uidB: string) =>
  [uidA, uidB].sort().join('_');

export interface ChatThreadRecord {
  participants: string[];
  lastMessage: string;
  lastSenderID: string;
  lastMessageAt?: Timestamp;
  lastIsRead?: 'true' | 'false';
  startedAt?: Timestamp;
}

const MAX_PREVIEW_LEN = 200;

/**
 * Update (or create) the per-thread summary doc as part of every chat
 * message write. Best-effort — never throws, never blocks the chat write.
 */
export const upsertChatThreadSummary = async (
  senderUID: string,
  receiverUID: string,
  message: string
): Promise<void> => {
  if (!senderUID || !receiverUID) return;
  const participants = [senderUID, receiverUID].sort();
  const id = chatThreadDocID(senderUID, receiverUID);
  // We deliberately do NOT pass { merge: true }. setDoc-with-merge
  // translates to a single mutation that the rules engine evaluates as
  // an `update` even when the doc doesn't exist yet — which makes our
  // strict update rule (`resource.data.participants` etc.) fail on
  // first write. Plain setDoc fires `create` when the doc is missing
  // and `update` otherwise. The summary is fully overwritten on each
  // write (it's derived from the latest message), so we never need to
  // preserve any prior server-side state.
  try {
    await setDoc(doc(db, CHAT_THREADS_COLLECTION, id), {
      participants,
      lastMessage: message.slice(0, MAX_PREVIEW_LEN),
      lastSenderID: senderUID,
      lastIsRead: 'false',
      lastMessageAt: serverTimestamp(),
      startedAt: serverTimestamp(),
    });
  } catch (err) {
    LoggerService.warn?.('upsertChatThreadSummary failed', err);
  }
};

/**
 * Mark the active thread as read for the local user. Called when the user
 * is actively viewing the chat. Updates the `lastIsRead` flag so the
 * sidebar can render an unread indicator.
 */
export const markChatThreadRead = async (
  myUID: string,
  otherUID: string
): Promise<void> => {
  if (!myUID || !otherUID) return;
  // updateDoc -> always evaluates the `update` rule, matching our strict
  // branch (b) "receiver flips lastIsRead -> 'true' only". If the thread
  // summary doesn't exist yet (e.g. brand-new chat the sender just
  // created), the update will 404 and we ignore it — the next message
  // they send will write a fresh summary.
  try {
    await updateDoc(
      doc(db, CHAT_THREADS_COLLECTION, chatThreadDocID(myUID, otherUID)),
      { lastIsRead: 'true' }
    );
  } catch (err) {
    LoggerService.warn?.('markChatThreadRead failed', err);
  }
};

/**
 * Delete the thread summary (called from dm-page's delete-thread action).
 */
export const deleteChatThreadSummary = async (
  uidA: string,
  uidB: string
): Promise<void> => {
  if (!uidA || !uidB) return;
  try {
    await deleteDoc(
      doc(db, CHAT_THREADS_COLLECTION, chatThreadDocID(uidA, uidB))
    );
  } catch (err) {
    LoggerService.warn?.('deleteChatThreadSummary failed', err);
  }
};

export interface ChatThreadSummary {
  id: string;
  otherUID: string;
  lastMessage: string;
  lastSenderID: string;
  lastMessageAt?: Timestamp;
  lastIsRead: 'true' | 'false';
}

/**
 * Live list of all chat threads the current user participates in,
 * ordered most-recent-first. One doc per thread — robust against the
 * "100 messages drowned the sidebar" bug.
 */
export const useChatThreads = (
  max: number = 100
): { threads: ChatThreadSummary[]; loading: boolean; isError: boolean } => {
  const { firebaseUID } = useSelector((s: Store) => s.userReducer);
  const [threads, setThreads] = useState<ChatThreadSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(!!firebaseUID);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!firebaseUID) {
      setThreads([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setIsError(false);

    const q = query(
      collection(db, CHAT_THREADS_COLLECTION),
      where('participants', 'array-contains', firebaseUID),
      orderBy('lastMessageAt', 'desc'),
      fsLimit(max)
    );

    const unsub = onSnapshot(
      q,
      snap => {
        const rows: ChatThreadSummary[] = [];
        snap.forEach(d => {
          const data = d.data() as ChatThreadRecord;
          const other =
            (data.participants || []).find(p => p !== firebaseUID) || '';
          if (!other) return;
          rows.push({
            id: d.id,
            otherUID: other,
            lastMessage: data.lastMessage || '',
            lastSenderID: data.lastSenderID || '',
            lastMessageAt: data.lastMessageAt,
            lastIsRead: data.lastIsRead || 'true',
          });
        });
        setThreads(rows);
        setLoading(false);
      },
      err => {
        LoggerService.error('chat threads listener failed', err);
        setIsError(true);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [firebaseUID, max]);

  return useMemo(
    () => ({ threads, loading, isError }),
    [threads, loading, isError]
  );
};
